import React from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { LazyMotion, domAnimation, m } from "framer-motion";

export interface HeadingBulletsData {
  sectionName?: string;
  topTitle?: string;
  title: string;
  smallTitle?: string;
  text: string;
  image: string;
  image_link?: string;
  alt?: string;
  bullets: any[];
}

interface HeadingBulletsProps {
  data: HeadingBulletsData;
}

export default function HeadingBullets({ data }: HeadingBulletsProps) {
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");
  const useCloudinary = !!data.image_link;
  const imageSrc = useCloudinary
    ? data.image_link
    : data.image
    ? getIconUrl(data.image)
    : null;
  const imageAlt = data.alt || "Section Illustration";

  return (
    <LazyMotion features={domAnimation}>
      <section
        id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
        data-name={data.sectionName}
        className="w-full flex flex-col desktop:flex-row bg-[#1B2940]"
      >
        <m.div
        className="w-full desktop:w-1/3 flex flex-col-reverse desktop:ml-[7.5vw] desktop:pr-[3vw] desktop:flex-col justify-start gap-6 md:gap-8 items-center pt-12 desktop:pt-32"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <m.div
          className="flex flex-col gap-6 md:gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="flex flex-col items-center gap-2 mb-4">
            {data.topTitle && (
              <m.div
                className="font-sans text-special-h2 uppercase text-secondary tracking-widest text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {data.topTitle}
              </m.div>
            )}
            <m.div
              className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two-light text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              {data.title}
            </m.div>
            {data.smallTitle && (
              <m.div
                className="font-sans text-special-h2 uppercase text-secondary tracking-widest text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {data.smallTitle}
              </m.div>
            )}
            <m.div
              className="font-sans text-paragraph-large text-white text-center mt-6 mb-6 max-w-md text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              dangerouslySetInnerHTML={{ __html: data.text }}
            />
          </div>
        </m.div>
        {imageSrc && (
          <m.div
            className="relative w-[180px] h-[260px] md:w-[220px] md:h-[320px] desktop:w-[260px] desktop:h-[380px]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              className="object-contain brightness-0 invert"
            />
          </m.div>
        )}
      </m.div>
      {/* Right: Bullets */}
      <m.div
        className="w-full desktop:w-2/3 flex flex-col justify-center p-6 md:py-[44px] md:px-12 desktop:p-32 desktop:pl-12 gap-6 md:gap-12 bg-[#5E6A79]"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {data.bullets.map((bullet, idx) => (
          <m.div
            key={idx}
            className="flex flex-row items-center gap-6 md:gap-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            {bullet.item.logo_url ||
              (bullet.item.logo && (
                <div className="w-[84px] min-w-[84px] h-[136px] flex items-center justify-center">
                  {(bullet.item.logo_url || bullet.item.logo) && (
                    <Image
                      src={
                        bullet.item.logo_url
                          ? bullet.item.logo_url
                          : getIconUrl(bullet.item.logo)
                      }
                      alt={bullet.item.alt || ""}
                      width={84}
                      height={136}
                      className="object-contain invert brightness-0"
                    />
                  )}
                </div>
              ))}
            <div className="flex flex-col gap-2">
              <div className="font-sans text-h3-mobile md:text-h3-tablet desktop:text-h3-desktop uppercase text-[#FFFFFF]">
                {bullet.item.title}
              </div>
              <div
                className="font-sans text-paragraph-medium text-[#FFFFFF] [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:text-left [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:text-left"
                dangerouslySetInnerHTML={{ __html: bullet.item.text }}
              ></div>
            </div>
          </m.div>
        ))}
      </m.div>
    </section>
    </LazyMotion>
  );
}
